import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { getBooks, getCategories, getAuthors } from "@/lib/api";
import BookCard from "@/components/BookCard";
import Seo from "@/components/Seo";

export default function Libros() {
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [categoria, setCategoria] = useState("");
  const [autor, setAutor] = useState("");
  const [idioma, setIdioma] = useState("");
  const [gratuito, setGratuito] = useState(false);

  useEffect(() => {
    getCategories().then(setCategories);
    getAuthors().then(setAuthors);
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (categoria) params.categoria = categoria;
    if (autor) params.autor = autor;
    if (idioma) params.idioma = idioma;
    if (gratuito) params.gratuito = true;
    const t = setTimeout(() => {
      getBooks(params)
        .then(setBooks)
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, categoria, autor, idioma, gratuito]);

  const hasFilters = q || categoria || autor || idioma || gratuito;
  const clearFilters = () => { setQ(""); setCategoria(""); setAutor(""); setIdioma(""); setGratuito(false); };

  const selectCls =
    "rounded-full border border-[#0f2544]/15 bg-white px-4 py-2.5 text-sm text-[#0f2544] outline-none focus:border-[#1f44e0] transition-colors";

  return (
    <>
      <Seo title="Libros" description="Descubre el catálogo de Editorial Harvest: literatura cristiana y obras con propósito." />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-14 pb-8">
        <p className="hv-overline">Catálogo</p>
        <h1 className="font-serif text-4xl md:text-5xl text-[#0f2544] mt-3">Nuestros libros</h1>
        <p className="text-[#4a4a45] mt-4 max-w-2xl font-medium">
          Descubre las historias, enseñanzas y voces que forman parte de Editorial Harvest.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center border-y border-[#0f2544]/10 py-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#a09b90]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por título o autor..."
              className="w-full rounded-full border border-[#0f2544]/15 bg-white pl-11 pr-4 py-2.5 text-sm outline-none focus:border-[#1f44e0] transition-colors"
              data-testid="books-search"
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)} className={selectCls} data-testid="filter-categoria">
              <option value="">Todas las categorías</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.nombre}</option>
              ))}
            </select>
            <select value={autor} onChange={(e) => setAutor(e.target.value)} className={selectCls} data-testid="filter-autor">
              <option value="">Todos los autores</option>
              {authors.map((a) => (
                <option key={a.id} value={a.id}>{a.nombre}</option>
              ))}
            </select>
            <select value={idioma} onChange={(e) => setIdioma(e.target.value)} className={selectCls} data-testid="filter-idioma">
              <option value="">Todos los idiomas</option>
              <option value="Español">Español</option>
              <option value="Inglés">Inglés</option>
              <option value="Francés">Francés</option>
            </select>
            <button
              type="button"
              onClick={() => setGratuito((v) => !v)}
              aria-pressed={gratuito}
              className={`rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${gratuito ? "bg-[#1f44e0] text-white border border-[#1f44e0]" : "border border-[#0f2544]/15 bg-white text-[#0f2544] hover:border-[#1f44e0]"}`}
              data-testid="filter-gratis"
            >
              Solo gratis
            </button>
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="rounded-full px-4 py-2.5 text-sm font-medium text-[#8a8a83] hover:text-[#1f44e0] transition-colors" data-testid="clear-filters">
                Limpiar filtros
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 md:py-16" data-testid="books-grid">
        {loading ? (
          <p className="text-[#8a8a83]">Cargando...</p>
        ) : books.length === 0 ? (
          <p className="text-[#8a8a83]">No se han encontrado libros con estos criterios.</p>
        ) : (
          <>
            <p className="text-sm text-[#8a8a83] mb-8">{books.length} libro(s)</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 md:gap-8">
              {books.map((b) => (
                <BookCard key={b.slug} book={b} />
              ))}
            </div>
          </>
        )}
      </section>
    </>
  );
}
